<template>
  <div
    class="board_main_container"
    ref="boardContainer"
    :style="{
      height: '100%'
    }"
  >
    <board-view
      :tasksMap="tasksMap"
      :projectResources="projectResources"
      :columns="columns"
      :colWidth="colWidth"
      :showFilterStub="showFilterStub"
      :columnDragging="columnDragging"
      :taskDragging="taskDragging"
      :collapsed="collapsed"
      :dragPutRestricted="dragPutRestricted"
      :canDragTasks="canDragTasks"
      :isBoardConfigLoading="isBoardConfigLoading"
      :boardHeight="boardHeight"
      :draftTasks="draftTasks"
      @onColumnDragStart="onColumnDragStart"
      @onColumnDrop="onColumnDrop"
      @taskDragStart="taskDragStart"
      @onTaskDrop="onTaskDrop"
      @onCollapse="onCollapse"
      @taskChange="taskChange"
      @createTask="createTask"
      @editField="editField"
    />

    <confirm-popup
      :show="confirmDeletionData.showPopup"
      :text="confirmDeletionData.text"
      :ok-button="{ type: 'destructive', title: locale('common_yes') }"
      :cancel-button="{ type: 'secondary', title: locale('common_cancel') }"
      :popup-width="440"
      @onClickOk="handleConfirmDeleteTask"
      @onClickCancel="handleCancelDeleteTask"
    />

    <context-menus
      @editField="editField"
      @moveColumn="moveColumn"
      @showTaskSettings="showTaskSettings"
      @taskChange="taskChange"
      @deleteTask="showDeleteConfirmationPopup"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import BoardView from './components/BoardView.vue';
import ConfirmPopup from '$$vueCmp/popups/coreConfirmPopup/coreConfirmPopup.vue';
import ContextMenus from './components/ContextMenus.vue';

import app from '$$app';
import log from '$$log';
import {
  applyDrag,
  compareTasks,
  taskToOptions,
  prepareTaskValue,
  changeAssign,
  mapTaskForBoard,
} from '../../helpers/boardHelpers.js';
import TaskSettingsController from '../taskSettings/controllers/index.js';
import routerHelper from '$$routerHelper';
import ganttFilterHelper from '../../helpers/gantt_filter';
import customHelper from '$$helpers/custom';
import dateHelper from '$$helpers/dateFormats';
import rights from '../../components/rights';
import constants from '$$helpers/constants';
// import EstimationWorker from '../../components/workers/EstimationWorker.js';
import calendarModule from '../../components/calendar';
import { checkPricingAccess } from '../../helpers/pricingHelper';
import CalendarWorker from '../../components/workers/CalendarWorker';
import globalStore from '$$store/main';

export default {
  name: 'Board',
  components: {
    BoardView,
    ConfirmPopup,
    ContextMenus,
  },
  provide() {
    return {
      getTasksMap: this.getTasksMap,
      toggleContextMenu: this.toggleContextMenu,
      showTaskSettings: this.showTaskSettings,
      isFieldDisabled: this.isFieldDisabled,
      getProjectName: this.getProjectName,
      hideFilterPopup: this.hideFilterPopup,
      hideProjectSettingsPopup: this.hideProjectSettingsPopup,
      getCalendarConfig: this.getCalendarConfig,
      getClosestDate: this.getClosestDate,
      checkPricingAccessOverdue: this.checkPricingAccessOverdue,
      closeContextMenus: this.closeContextMenus,
      hideResourcePopup: this.hideResourcePopup
    };
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      locale: __,
      ganttId: 0,
      boardTasks: [],
      columns: [],
      tasksMap: {},
      collapsed: {},
      colWidth: 324,
      minColWidth: 256,
      maxColWidth: 324,
      resizeObserver: null,
      appListeners: [],
      taskPendingData: null,
      confirmDeletionData: {},
      dragPutRestricted: [],
      taskDragging: false,
      columnDragging: false,
      storeUnsubscribe: null,
      calendarConfig: null,
      isTaskCreating: false,
      rightsAccessCache: {},
      boardHeight: 1500,
      draftTasks: [],
      draftCleanupTimeout: {},
      updateTaskTimeout: null
    };
  },
  created() {
    this.ganttId = +this.$route.params.projectId;
    this.rightsAccessCache = {
      static_fields: rights.project.hasRight(this.ganttId, 'static_fields'),
      static_fields_4_only: rights.project.hasRight(this.ganttId, 'static_fields_4_only'),
      delete_task: rights.project.hasRight(this.ganttId, 'delete_task'),
      comments: rights.project.hasRight(this.ganttId, 'comments'),
      create_task: rights.project.hasRight(this.ganttId, 'create_task'),
      custom_field_edit: rights.project.hasRight(this.ganttId, 'custom_field_edit'),
      all_tasks: rights.project.hasRight(this.ganttId, 'all_tasks'),
    };

    this.storeUnsubscribe = globalStore.subscribe(async ({ type, payload }, state) => {
      if (type === 'tasksModel/updateTask' && payload.gantt_id === this.ganttId) {
        const draftIndex = this.draftTasks.findIndex(t => t.taskId === payload.id);

        if (this.draftTasks.length && draftIndex > -1 && this.groupingFieldInfo.key !== 'resources') {
          if (this.groupingFieldInfo.key === 'status') {
            const updated = {
              storeStatus: payload.status,
              storeProgress: payload.progress
            };

            const newDraftInfo = {
              ...this.draftTasks[draftIndex],
              ...updated
            };

            this.draftTasks[draftIndex] = newDraftInfo;

            if (!(newDraftInfo.status === newDraftInfo.storeStatus && newDraftInfo.progress === newDraftInfo.storeProgress)) {
              return;
            }
          }

          this.cleanupDraftTask(payload);
        } else {
          if (this.updateTaskTimeout) {
            clearTimeout(this.updateTaskTimeout);
            this.updateTaskTimeout = setTimeout(() => {
              this.initProjectTasks();
              this.setColWidth(this.$refs.boardContainer, this.groupingFieldInfo?.options?.length || 1);
              this.$nextTick(() => {
                this.prepareGroupedTasks();
                this.updateTaskTimeout = null;
              });
            }, 500);
          } else {
            if (payload.type !== 'task') {
              this.updateTaskInColumns(payload);
            } else {
              this.updateTaskTimeout = setTimeout(() => {
                const task = this.storeTasks.find(t => t.id === payload.id);
                task && this.updateTaskInColumns(task);
                this.updateTaskTimeout = null;
              }, 500);
            }
          }
        }
      }

      if (['tasksModel/updateTasks', 'tasksModel/addTasks', 'tasksModel/deleteTasks'].includes(type)) {
        const filteredTasks = payload.tasks.filter(t => t.gantt_id === this.ganttId);

        if (filteredTasks.length > 300) {
          await this.initProjectTasks();

          this.$nextTick(() => {
            this.prepareGroupedTasks();
          });
        } else {
          filteredTasks.forEach(task => {
            const mode = type === 'tasksModel/addTasks' ? 'add' : type === 'tasksModel/deleteTasks' ? 'remove' : 'update';

            if (mode === 'add' && !this.draftTasks.find(t => t.id === 'draft' && t.text === task.text)) {
              globalStore.dispatch('board/addNewTaskToPool', task.id);
            }

            this.updateTaskInColumns(task, mode);
          });
        }
      }

      if (type === 'tasksModel/addTask' && payload.gantt_id === this.ganttId) {
        if (!this.draftTasks.length) {
          globalStore.dispatch('board/addNewTaskToPool', payload.id);
          this.updateTaskInColumns(payload, 'add');
        } else {
          let currentDraftIndex = this.draftTasks.findIndex(t => t.id === 'draft' && !t.taskId);

          if (currentDraftIndex === -1) return;

          this.draftTasks[currentDraftIndex] = {
            ...this.draftTasks[currentDraftIndex],
            taskId: payload.id,
          };

          const task = payload;
          const key = this.groupingFieldInfo.isCustom ? this.groupingFieldInfo.customFieldId : this.groupingFieldInfo.key;
          let value = '';

          if (key === 'resources') {
            if (this.draftTasks[currentDraftIndex].resources.length) {
              changeAssign({
                task,
                rawValue: this.draftTasks[currentDraftIndex].resources.map(res => res.resource_id),
                $store: globalStore,
              });
            } else {
              this.cleanupDraftTask(payload);
            }
          } else {
            value = prepareTaskValue({
              task,
              key,
              rawValue: this.draftTasks[currentDraftIndex][key],
              field: this.groupingFieldInfo,
            });

            if (value) {
              let timeout = 0;

              if (this.groupingFieldInfo.key === 'status') {
                timeout = 500;

                if (value === 1) {
                  this.cleanupDraftTask(payload);
                  return;
                }
              }

              setTimeout(() => {
                this.taskChange({
                  taskData: task,
                  key,
                  value,
                });
              }, timeout);
            } else {
              this.cleanupDraftTask(payload);
            }
          }
        }
      }

      if (type === 'tasksModel/deleteTask' && payload.gantt_id === this.ganttId) {
        if (this.newTasksPool.includes(payload.id)) {
          globalStore.dispatch('board/removeNewTaskFromPool', payload.id);
        }

        if (payload.id === this.confirmDeletionData.id) {
          this.confirmDeletionData = {};
        }

        this.updateTaskInColumns(payload, 'remove');
      }

      if (type === 'tasksModel/updateCustomValues' && payload.ganttId === this.ganttId && payload.taskId) {
        const newData = {
          ...(payload.dataByGanttId?.[payload.taskId] || payload?.newValue),
          id: payload.taskId
        };

        const draftIndex = this.draftTasks.findIndex(t => t.taskId === payload.taskId);

        if (this.draftTasks.length && draftIndex > -1) {
          if (this.groupingFieldInfo.key === 'status') {
            const updated = {};

            if (newData.status) {
              updated.storeStatus = newData.status;
            }

            if (newData.progress) {
              updated.storeProgress = newData.progress;
            }

            const newDraftInfo = {
              ...this.draftTasks[draftIndex],
              ...updated
            };

            this.draftTasks[draftIndex] = newDraftInfo;

            if (!(newDraftInfo.status === newDraftInfo.storeStatus && newDraftInfo.progress === newDraftInfo.storeProgress)) {
              return;
            }
          }

          this.cleanupDraftTask(newData);
        } else {
          if (this.updateTaskTimeout) {
            clearTimeout(this.updateTaskTimeout);
            this.updateTaskTimeout = setTimeout(() => {
              this.initProjectTasks();
              this.setColWidth(this.$refs.boardContainer, this.groupingFieldInfo?.options?.length || 1);
              this.$nextTick(() => {
                this.prepareGroupedTasks();
                this.updateTaskTimeout = null;
              });
            }, 500);
          } else {
            if (this.tasksMap[newData.id]?.type !== 'task') {
              this.updateTaskInColumns(newData);
            } else {
              this.updateTaskTimeout = setTimeout(() => {
                this.updateTaskInColumns(newData);
                this.updateTaskTimeout = null;
              }, 500);
            }
          }
          // this.updateTaskInColumns(newData);
        }
      }

      if (type === 'tasksModel/updateTaskResourcesByGanttId' && payload.ganttId === this.ganttId) {
        if (payload.taskId) {
          const newData = {
            resources: payload.resourcesToTasks[payload.taskId] || [],
            id: +payload.taskId
          };

          if (!this.draftTasks.length) {
            this.updateTaskInColumns(newData);
          } else if (this.draftTasks.findIndex(t => t.taskId === payload.taskId) > -1 && this.groupingFieldInfo.key !== 'status') {
            this.cleanupDraftTask(newData);
          }
        } else {
          if (payload.resourcesToTasks && Object.keys(payload.resourcesToTasks).length * this.boardTasks.length > 10000) {
            this.initProjectTasks();
            this.setColWidth(this.$refs.boardContainer, this.groupingFieldInfo?.options?.length || 1);
            this.$nextTick(() => {
              this.prepareGroupedTasks();
            });
          } else {
            Object.keys(payload.resourcesToTasks).forEach(taskId => {
              const newData = {
                resources: payload.resourcesToTasks[taskId] || [],
                id: +taskId
              };

              if (!this.draftTasks.length) {
                this.updateTaskInColumns(newData);
              } else if (this.draftTasks.findIndex(t => t.taskId === taskId) > -1 && this.groupingFieldInfo.key !== 'status') {
                this.cleanupDraftTask(newData);
              }
            });
          }
        }
      }

      if (type === 'columns/changeCustomValues'
        && payload.task.gantt_id === this.ganttId
        && this.customFields.find(field => field.customFieldId === payload.columnId)
      ) {
        const customValue = payload.value.length ? payload.value.map((item) => isNaN(+item) ? item : +item) : [payload.value];
        const newData = {
          id: payload.task.id,
          [payload.columnId]: customValue
        };

        if (!this.draftTasks.length) {
          this.updateTaskInColumns(newData);
        } else if (this.draftTasks.findIndex(t => t.taskId === payload.task.id) > -1 && this.groupingFieldInfo.key !== 'status') {
          this.cleanupDraftTask(newData);
        }
      }

      if (type === 'columns/updateUserCustomValues') {
        payload.userCustomValues.forEach(task => {
          if (task.ganttId === this.ganttId) {
            const newData = { id: task.taskId };

            task.customFields.forEach(column => {
              if (this.customFields.find(field => field.customFieldId === column.customColumnId)) {
                const customValue = Array.isArray(column.value) ? column.value.map((item) => +item) : [+column.value];
                newData[column.customColumnId] = customValue;
              }
            });

            if (Object.keys(newData).length > 1) {
              this.updateTaskInColumns(newData);
            }
          }
        });
      }

      if (type === 'columns/updateCustomColumn' && this.customFields.find(field => field.customFieldId === payload.columnId)) {
        this.updateCustomField(payload);
      }

      if (
        type === 'columns/removeColumnFromProjects'
        && this.customFields.find(field => field.customFieldId === payload.columnId)
        && payload.projectIds.includes(this.ganttId)
      ) {
        this.updateCustomField(payload, 'remove');
      }

      if (type === 'columns/removeCustomColumn' && this.customFields.find(field => field.customFieldId === payload)) {
        this.updateCustomField({ columnId: payload }, 'remove');
      }

      if (
        type === 'columns/updateProject'
        && payload.projectId === this.ganttId
      ) {
        if (payload.updated?.is_archived) {
          return;
        }

        let removedColumn = null;
        this.customFields.forEach((field) => {
          if (!payload.updated?.columns?.includes(field.customFieldId)) {
            removedColumn = field.customFieldId;
          }
        });

        if (removedColumn) {
          this.updateCustomField({
            columnId: removedColumn
          }, 'remove');
        }

      }

      if (type === 'columns/addColumnsToProjects' && payload.projectIds.includes(this.ganttId)) {
        this.updateCustomField(payload, 'add');
      }

      if (type === 'resourcesModel/updateResource' && this.groupingFieldInfo.key === 'resources') {
        this.updateResourceInColumns(
          payload
        );
      }

      if (type === 'resourcesModel/addResources' && payload.find(r => r.resourceProjects.find(p => p.projectId === this.ganttId))) {
        this.prepareGroupedTasks();
      }

      if (type === 'resourcesModel/addResourceToProject' && payload.projectId === this.ganttId) {
        this.prepareGroupedTasks();
      }

      if (type === 'resourcesModel/addResourceToProjects' && payload.resourceProjects.find(p => p.projectId === this.ganttId)) {
        this.prepareGroupedTasks();
      }

      if (type === 'resourcesModel/removeResourceFromProject' && payload.projectId === this.ganttId) {
        const wasUsedInGrouping = this.groupingFieldInfo.key === 'resources' && this.columns.find(col => col.id === payload.resourceId);

        if (wasUsedInGrouping) {
          this.initProjectTasks();
          this.setColWidth(this.$refs.boardContainer, this.groupingFieldInfo?.options?.length || 1);
          this.$nextTick(() => {
            this.prepareGroupedTasks();
          });
        }
      }

      if (type === 'resourcesModel/removeResources') {
        const wasUsedInGrouping = this.groupingFieldInfo.key === 'resources' && this.columns.find(col => payload.includes(col.id));

        if (wasUsedInGrouping) {
          this.initProjectTasks();
          this.setColWidth(this.$refs.boardContainer, this.groupingFieldInfo?.options?.length || 1);
          this.$nextTick(() => {
            this.prepareGroupedTasks();
          });
        }
      }
    });

    const appListener_1 = app.on('header:refresh:filter', () => {
      if (this.newTasksPool.length) {
        this.clearNewTasksPool();
        this.prepareGroupedTasks();
      }
    });

    const appListener_2 = app.on('board:sort:refresh', () => {
      if (this.newTasksPool.length) {
        this.clearNewTasksPool();
        this.prepareGroupedTasks();
      }
    });

    const appListener_3 = app.on('onAfterCollaboration', async (data) => {
      if (data.event === 'TaskResourceUnassigned' && data.projects?.includes(this.ganttId)) {
        if (data.unassignedResources) {
          if (data.unassignedResources.length * this.boardTasks.length > 10000) {
            this.initProjectTasks();
            this.setColWidth(this.$refs.boardContainer, this.groupingFieldInfo?.options?.length || 1);
            this.$nextTick(() => {
              this.prepareGroupedTasks();
            });
          } else {
            data.unassignedResources?.forEach(item => {
              if (this.tasksMap[item.taskId]) {
                const newResources = this.tasksMap[item.taskId].resources.filter(res => !item.unassignedResourcesIds.includes(res.resource_id));
                this.updateTaskInColumns({
                  resources: newResources,
                  id: +item.taskId
                });
              }
            });
          }
        }
      }

      if (data.event === 'HistoryRestored' && data.projects?.includes(this.ganttId)) {
        await this.initProjectTasks();
        this.setColWidth(this.$refs.boardContainer, this.groupingFieldInfo?.options?.length || 1);
        this.$nextTick(() => {
          this.prepareGroupedTasks();
        });
      }
    });

    const appListener_4 = app.on('project-calendar:change', (ganttId) => {
      if (this.ganttId === ganttId) {
        this.calendarConfig = calendarModule.getCalendarConfig(this.ganttId);
      }
    });

    const appListener_5 = app.on('customDays:insert', (ganttId = null) => {
      if (this.ganttId === ganttId) {
        this.calendarConfig = calendarModule.getCalendarConfig(this.ganttId);
      }
    });

    this.appListeners = [
      appListener_1,
      appListener_2,
      appListener_3,
      appListener_4,
      appListener_5,
    ];
  },
  beforeUnmount() {
    this.tasksMap = {};
    this.storeUnsubscribe?.();
    this.resizeObserver?.unobserve(this.$refs.boardContainer);
    this.appListeners.forEach(listener => app.off(listener));
    this.hideResourcePopup();
    globalStore.dispatch('board/resetState');
    globalStore.commit('board/setLoading', true);
  },
  computed: {
    currentResourceId() {
      return this.getResourceIdByUserId(user.settings.user_id) || 0;
    },
    storeTasks() {
      return this.getTasksByGanttIds([this.ganttId]);
    },
    canDragTasks() {
      let canDragInCurrentGrouping = true;

      if (this.groupingFieldInfo.key === 'status') {
        canDragInCurrentGrouping = this.rightsAccessCache.static_fields || this.rightsAccessCache.static_fields_4_only;
      } else if (this.groupingFieldInfo.isCustom) {
        canDragInCurrentGrouping = this.rightsAccessCache.custom_field_edit;
      } else {
        canDragInCurrentGrouping = this.rightsAccessCache.static_fields;
      }

      return canDragInCurrentGrouping && !(this.taskCardEditing || this.taskSettingsInfo);
    },
    isProjectArchived() {
      return globalStore.getters['columns/getProjectById'](this.ganttId).is_archived;
    },
    projectResources() {
      return globalStore.getters['resourcesModel/getResourcesByProjectId'](this.ganttId).reduce((acc, res) => {
        acc[res.id] = res;

        return acc;
      }, {});
    },
    filterTasksCallback() {
      return ganttFilterHelper.getFilter({
        options: this.currentFilter?.properties,
        value: this.currentFilter?.properties.text || '',
      }, null);
    },
    showFilterStub() {
      return !!(this.isFilterActive && this.columns.length && this.columns.every((col) => !col.tasks.length));
    },
    ...mapGetters({
      isBoardConfigLoading: 'board/isLoading',
      groupingFieldInfo: 'board/groupingFieldInfo',
      sortingFieldInfo: 'board/sortingFieldInfo',
      customFields: 'board/customFields',
      customSort: 'board/customSort',
      boardConfig: 'board/currentConfig',
      newTasksPool: 'board/newTasksPool',
      taskCardEditing: 'board/taskCardEditing',
      taskSettingsInfo: 'board/taskSettingsInfo',
      contextMenus: 'board/contextMenus',
      getValuesMapForTask: 'columns/getValuesMapForTask',
      getValuesMapByProjectId: 'columns/getValuesMapByProjectIdForBoard',
      getCustomColumnById: 'columns/getCustomColumnById',
      getProjectsNumberByColumnId: 'columns/getProjectsNumberByColumnId',
      getTasksByGanttIds: 'tasksModel/getTasksByGanttIds',
      getProjectItem: 'tasksModel/getItem',
      getDataForBreadcrumbsByParentId: 'tasksModel/getDataForBreadcrumbsByParentId',
      getResourcesByProjectId: 'resourcesModel/getResourcesByProjectId',
      isFilterActive: 'filter/isActive',
      currentFilter: 'filter/currentFilter',
      projectName: 'headerView/headTitle',
      isOverdueTasks: 'headerView/isOverdueTasks',
      getResourceIdByUserId: 'resourcesModel/getResourceIdByUserId',
    }),
  },
  watch: {
    groupingFieldInfo(newVal, oldVal) {
      if (this.isBoardConfigLoading) return;

      const newFieldId = newVal.id || newVal.customFieldId;
      const oldFieldId = oldVal.id || oldVal.customFieldId;

      if (newFieldId === oldFieldId && newVal.isCustom === oldVal.isCustom) return;

      if (!newFieldId) {
        return;
      }

      if (this.newTasksPool.length) {
        this.clearNewTasksPool();
      }

      this.prepareGroupedTasks();
    },
    sortingFieldInfo(newVal, oldVal) {
      if (this.isBoardConfigLoading) return;

      if (`${newVal.id}_${newVal.direction}` === `${oldVal.id}_${oldVal.direction}`) return;

      if (this.newTasksPool.length) {
        this.clearNewTasksPool();
      }

      this.prepareGroupedTasks();
    },
    currentFilter(val) {
      if (!val || val.type !== 'boardview' || val.project_id !== this.ganttId) return;

      if (this.newTasksPool.length) {
        this.clearNewTasksPool();
      }

      this.prepareGroupedTasks();
    },
    columns(newValue) {
      if (newValue.length) {
        this.setColWidth(this.$refs.boardContainer, newValue.length);
      }
    },
    isBoardConfigLoading(newValue) {
      if (!newValue) {
        this.initBoard();
      }
    },
    $route: {
      handler(to, from) {
        const taskId = +to.params.taskId;

        if (!taskId) {
          globalStore.dispatch('board/setTaskSettingsInfo', null);
        } else if (this.taskSettingsInfo?.taskId !== taskId){
          globalStore.dispatch('board/setTaskSettingsInfo', { taskId });

          if (this.taskCardEditing) {
            globalStore.dispatch('board/setTaskCardEditing', null);
            this.hideResourcePopup();
          }

          if (this.contextMenus.length) {
            this.closeContextMenus();
          }
        }
      },
      immediate: true,
    },
    showFilterStub(flag) {
      globalStore.commit('filter/isNotFound', flag);
    },
    isOverdueTasks(newValue) {
      if (newValue === this.boardConfig.overdue) return;

      globalStore.dispatch('board/setBoardConfig', {
        config: {
          overdue: newValue
        }
      });
    }
  },
  methods: {
    getTasksMap() {
      return this.tasksMap;
    },
    async initBoard() {
      const t = log.time('viewBoard.init');
      globalStore.commit('headerView/setOverdueTasks', !!this.boardConfig.overdue);
      this.initResizeObserver();
      globalStore.dispatch('board/setDatesWidth', this.calculateDatesWidth());
      this.initCollapsedState();

      if ((!this.groupingFieldInfo.id && !this.groupingFieldInfo.customFieldId)
        || (this.groupingFieldInfo.isCustom && !checkPricingAccess('custom_fields'))
      ) {
        globalStore.dispatch('board/setDefaultGroupingConfig');
      }

      await this.initProjectTasks();
      this.setColWidth(this.$refs.boardContainer, this.groupingFieldInfo?.options?.length || 1);
      this.calendarConfig = calendarModule.getCalendarConfig(this.ganttId);
      this.$nextTick(() => {
        this.prepareGroupedTasks();
      });
      t.end();
    },
    async initProjectTasks() {
      const t = log.time('viewBoard.initProjectTasks');
      const projectData = this.getProjectItem(this.ganttId);
      const tasks = projectData.tasks || [];

      const boardTasks = [];
      const tasksMap = {};

      const { tasksWBSCodes } = await TaskSettingsController.getTasksWbsCodes([this.ganttId]);
      const tasksCustomValues = this.getValuesMapByProjectId(this.ganttId);

      tasks.forEach(storeTask => {
        const task = { ...storeTask };

        if (projectData.resourcesToTasks[task.id]) {
          task.resources = projectData.resourcesToTasks[task.id];
        }

        if (this.shouldDisplayTaskOnBoard(task)) {
          const preparedTask = mapTaskForBoard({
            task,
            visibleOnBoard: true,
            projectResources: this.projectResources,
            customFields: this.customFields,
            customValues: tasksCustomValues,
            wbsCode: tasksWBSCodes[task.id]
          });

          tasksMap[task.id] = preparedTask;
          if (!this.newTasksPool.includes(preparedTask.id)) {
            boardTasks.push(preparedTask.id);
          }
        } else {
          tasksMap[task.id] = mapTaskForBoard({ task });
        }
      });

      this.tasksMap = tasksMap;
      this.boardTasks = boardTasks;
      t.end();
    },
    initResizeObserver() {
      this.resizeObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          this.boardHeight = entry.target.clientHeight;

          if (this.columns.length) {
            this.setColWidth(entry.target, this.columns.length);
          }
        });
      });

      this.boardHeight = this.$refs.boardContainer.clientHeight;
      this.resizeObserver.observe(this.$refs.boardContainer);
    },
    initCollapsedState() {
      this.collapsed = (this.boardConfig.grouping?.[0]?.order || []).reduce((acc, column) => {
        acc[column.id] = column.isCollapsed || false;

        return acc;
      }, {});
    },
    async prepareGroupedTasks() {
      let boardTasks = this.boardTasks.map((id) => this.tasksMap[id]);
      const boardColumns = [...this.groupingFieldInfo.options] || [];
      const columnsMap = new Map();
      if (boardColumns.length) {
        boardColumns.reduce((acc, column) => {
          acc.set(column.id, {
            ...column,
            tasks: [],
            totalHeight: 0,
          });

          return acc;
        }, columnsMap);
      }

      if (this.newTasksPool.length) {
        const newTasksMap = this.newTasksPool.reduce((acc, taskId) => ({ ...acc, [taskId]: true }), {});

        boardTasks = boardTasks.filter(task => !newTasksMap[task.id]);
      }

      if (this.isFilterActive) {
        boardTasks = boardTasks.filter(this.filterTasksCallback);
      }
      boardTasks.sort(compareTasks({
        ...this.sortingFieldInfo,
        resources: this.getResourcesByProjectId(this.ganttId)
      }));

      if (this.newTasksPool.length) {
        boardTasks.push(...this.newTasksPool.map((id) => this.tasksMap[id]));
      }

      if (columnsMap) {
        boardTasks.forEach(task => {
          const taskOpts = taskToOptions({ task, groupingFieldInfo: this.groupingFieldInfo });

          taskOpts.forEach((opt, index) => {
            let taskObj = { ...task };
            const customSort = [...this.customSort];
            const column = columnsMap.get(opt);

            // тут идет наполнения колонок тасками
            // идет уставнока position для каждой таски

            if (column && customSort.length > 0 && customSort.find(item => +item.groupKey === column.id)) {
              const group = customSort.find(item => +item.groupKey === column.id);
              const itemExist = group.tasks.find(item => item && item.taskId === taskObj.id);

              if (itemExist) {
                taskObj = { ...taskObj, position: itemExist.position };
              }
            }

            if (column) {
              taskObj.taskHeight = this.calculateTaskHeight(taskObj.id);
              taskObj.offsetTop = column.totalHeight + (column.tasks.length ? 8 : 0);
              column.totalHeight += taskObj.taskHeight + (column.tasks.length ? 8 : 0);
              column.tasks.push(taskObj);
            }
          });
        });
      }
      const newColumns = Array.from(columnsMap, ([key, value]) => value);

      // если есть кастомая сортировка, то сортируем таски в каждой колонке
      if (this.customSort.length > 0 && !this.sortingFieldInfo.id) {
        newColumns.forEach(column => {
          if (column.tasks.length && this.customSort.find(item => +item.groupKey === column.id)) {
            column.tasks.sort(compareTasks({
              key: 'custom',
              resources: this.getResourcesByProjectId(this.ganttId),
            }));

            column.totalHeight = 0;
            column.tasks.forEach((task, i) => {
              task.offsetTop = column.totalHeight + (i ? 8 : 0);
              column.totalHeight += task.taskHeight + (i ? 8 : 0);
            });
          }
        });
      }

      this.columns = newColumns;

      if (this.taskCardEditing) {
        globalStore.dispatch('board/setTaskCardEditing', null);
        this.hideResourcePopup();
      }

      if (this.contextMenus.length) {
        this.closeContextMenus();
      }
    },
    async updateTaskInColumns(newData, mode) {
      const isNew = this.newTasksPool.some((id) => id === newData.id);
      const isDeleted = mode === 'remove';
      if (!this.tasksMap[newData.id] && !isNew) return;

      const combinedTaskData = { ...(this.tasksMap[newData.id] || {}), ...newData };

      if (!this.shouldDisplayTaskOnBoard(combinedTaskData)) {
        const mappedTask = mapTaskForBoard({
          task: combinedTaskData
        });

        if (this.tasksMap[newData.id] && this.shouldDisplayTaskOnBoard(this.tasksMap[newData.id])) {
          const boardTaskIndex = this.boardTasks.indexOf(newData.id);
          if (boardTaskIndex > -1) {
            this.boardTasks.splice(boardTaskIndex, 1);
          }

          if (isNew) {
            globalStore.dispatch('board/removeNewTaskFromPool', newData.id);
          }

          const taskOptsOld = taskToOptions({ task: this.tasksMap[newData.id], groupingFieldInfo: this.groupingFieldInfo });
          this.columns.forEach(col => {
            if (taskOptsOld.includes(col.id)) {
              let newTasksArray = [];
              let totalHeight = 0;
              col.tasks.forEach((task, i) => {
                if (task.id === newData.id) return;
                task.offsetTop = totalHeight + (i ? 8 : 0);
                totalHeight += task.taskHeight + (i ? 8 : 0);
                newTasksArray.push(task);
              });
              col.totalHeight = totalHeight;
              col.tasks = newTasksArray;
            }
          });
        }

        this.tasksMap[newData.id] = mappedTask;

        return;
      }

      const oldTaskData = { ...this.tasksMap[newData.id] };
      const mappedTask = mapTaskForBoard({
        task: combinedTaskData,
        visibleOnBoard: true,
        projectResources: this.projectResources,
        customFields: this.customFields,
        customValues: this.getValuesMapForTask(this.ganttId, newData.id)
      });
      const taskOptsOld = (isNew && mode === 'add') || !this.shouldDisplayTaskOnBoard(oldTaskData) ? [] : taskToOptions({ task: oldTaskData, groupingFieldInfo: this.groupingFieldInfo });
      const taskOptsNew = isDeleted ? [] : taskToOptions({ task: mappedTask, groupingFieldInfo: this.groupingFieldInfo });

      const isProgressDumbUpdate =
        (mappedTask.progress === 0 && mappedTask.status !== 1) ||
        (mappedTask.progress > 0 && mappedTask.progress < 1 && mappedTask.status !== 2) ||
        (mappedTask.progress === 1 && ![3,4].includes(mappedTask.status));

      if (isDeleted) {
        delete this.tasksMap[mappedTask.id];

        const deletedIndex = this.boardTasks.indexOf(mappedTask.id);
        if (deletedIndex > -1) {
          this.boardTasks.splice(deletedIndex, 1);
        }
      } else {
        const updatedTask = { ...mappedTask };

        if (isProgressDumbUpdate) {
          updatedTask.status = oldTaskData.status;
        }

        this.tasksMap[mappedTask.id] = updatedTask;

        const boardTaskIndex = this.boardTasks.indexOf(mappedTask.id);
        if (boardTaskIndex === -1 && !isNew) {
          this.boardTasks.splice(this.boardTasks.length, 0, mappedTask.id);
        }
      }

      if (isProgressDumbUpdate) return;

      this.columns.forEach((col) => {
        let newTasksArray = null;
        let group = null;
        let taskExists = false;

        if (taskOptsOld.includes(col.id) && !taskOptsNew.includes(col.id)) {  // удаляем задачу из колонки если раньше она была в ней, а теперь нет
          newTasksArray = col.tasks.filter(task => task.id !== mappedTask.id);
        } else if (taskOptsNew.includes(col.id)) {  // если задача сейчас должна быть в этой колонке
          group = this.customSort.length > 0 && this.customSort.find(item => +item.groupKey === col.id);
          newTasksArray = [];

          col.tasks.forEach(task => {
            let newData = { ...task };

            if (newData.id === mappedTask.id) {  // если нашли в колонке задачу - обновляем
              if (!isNew && !this.filterTasksCallback(mappedTask)) {
                return;
              }

              newData = { ...task, ...mappedTask };
              taskExists = true;
            }

            if (group && !this.sortingFieldInfo.id) {
              const itemExist = group.tasks.find(item => item && item.taskId === newData.id);

              if (itemExist) {
                newData.position = itemExist.position;
              }
            }

            newTasksArray.push(newData);
          });

          if (!taskExists && (this.filterTasksCallback(mappedTask) || isNew)) { // если не нашли в колонке задачу - добавляем
            newTasksArray.push({
              ...mappedTask,
              taskHeight: this.calculateTaskHeight(mappedTask.id)
            });
          }
        }

        if (newTasksArray) {
          if (group && !this.sortingFieldInfo.id) {
            if (taskExists) {
              newTasksArray.sort(compareTasks({
                key: 'custom',
                resources: this.getResourcesByProjectId(this.ganttId),
              }));
            } else if (this.filterTasksCallback(mappedTask) || isNew) {
              const position = parseInt(Math.max(...group.tasks.map(t => t.position)), 10) + 1;
              newTasksArray[newTasksArray.length - 1].position = position;

              globalStore.dispatch('board/setCustomSort', {
                columnId: col.id,
                tasks: newTasksArray,
                addedIndex: newTasksArray.length - 1,
                fullIndexing: false,
              });
            }
          } else if (!isNew) {
            newTasksArray.sort(compareTasks({
              ...this.sortingFieldInfo,
              resources: this.getResourcesByProjectId(this.ganttId)
            }));
          }

          let totalHeight = 0;
          newTasksArray.forEach((task, i) => {
            task.offsetTop = totalHeight + (i ? 8 : 0);
            totalHeight += task.taskHeight + (i ? 8 : 0);
          });
          col.totalHeight = totalHeight;
          col.tasks = newTasksArray;
        }
      });

      const isTaskGroupingChanged = taskOptsOld.join(',') !== taskOptsNew.join(',');
      const isTaskSortingChanged = this.sortingFieldInfo.id && !isNew && compareTasks({
        ...this.sortingFieldInfo,
        resources: this.getResourcesByProjectId(this.ganttId),
      })(oldTaskData, mappedTask);
      const isEditingTaskUpdated = !isNew && mappedTask.id === this.taskCardEditing?.taskId;

      if (isTaskGroupingChanged || isTaskSortingChanged || isEditingTaskUpdated) {
        if (this.taskCardEditing) {
          globalStore.dispatch('board/setTaskCardEditing', null);
          this.hideResourcePopup();
        }

        if (this.contextMenus.length) {
          this.closeContextMenus();
        }
      }
    },
    shouldDisplayTaskOnBoard(task) {
      return ['task'].includes(task.type)
        && (this.rightsAccessCache.all_tasks
          || task.resources.find((res) => res.resource_id === this.currentResourceId)
        );
    },
    updateResourceInColumns(payload) {
      if (this.groupingFieldInfo.key !== 'resources') return;

      this.columns.forEach((col) => {
        if (col.resourceId && col.resourceId === payload.resourceId) {
          const { name, photo } = payload.updated;

          if (col.value !== name) {
            col.value = name;

            if (col.name) {
              col.name = name;
            }
          }

          if (col.picture !== photo) {
            col.picture = photo;
          }
        }
      });
    },
    async updateCustomField(payload, mode) {
      const isDeleted = mode === 'remove';
      const isGroupedBy = globalStore.getters['board/groupingFields'].find((field) => field.isGroupedBy).id === payload.columnId;
      await globalStore.dispatch('board/setAllFields', this.ganttId);

      if (isGroupedBy && isDeleted) {
        globalStore.dispatch('board/setDefaultGroupingConfig');
      }

      const tasksCustomValues = this.getValuesMapByProjectId(this.ganttId);

      [...this.boardTasks, ...this.newTasksPool].forEach((taskId) => {
        this.tasksMap[taskId] = mapTaskForBoard({
          task: this.tasksMap[taskId],
          visibleOnBoard: true,
          projectResources: this.projectResources,
          customFields: this.customFields,
          customValues: tasksCustomValues,
        });
      });

      if (isGroupedBy || isDeleted) {
        this.prepareGroupedTasks();

        if (this.taskCardEditing) {
          globalStore.dispatch('board/setTaskCardEditing', null);
        }

        if (this.contextMenus.length) {
          this.closeContextMenus();
        }
      }
    },
    onColumnDrop(event) {
      this.columnDragging = false;
      if (event.addedIndex === null && event.removedIndex === null) return;

      const reorderedColumns = applyDrag([...this.columns], event).updated;

      this.columns = reorderedColumns;
      globalStore.dispatch('board/setBoardConfig', {
        config: {
          grouping: [{
            ...globalStore.getters['board/currentConfig'].grouping[0],
            order: reorderedColumns.map(({ id }, i) => ({
              id,
              order: i + 1,
              isCollapsed: this.collapsed[id] || false,
            })),
          }],
        },
      });
    },
    onTaskDrop({
      dragItem, columnFrom, indexFrom, columnTo, indexTo,
    }) {
      this.taskDragging = false;
      this.dragPutRestricted = [];

      if (!this.canDragTasks) {
        return;
      }

      if (columnFrom === columnTo) {
        if (indexFrom === indexTo) return;

        if (indexFrom !== indexTo && this.sortingFieldInfo.key) {
          globalStore.dispatch('board/clearCustomSort');
          globalStore.dispatch('board/setBoardConfig', {
            config: {
              ...globalStore.getters['board/currentConfig'],
              sorting: {
                values: [],
              },
            },
          });
        }
      }

      const updatedColumns = [...this.columns].map(column => {
        let updatedTasks = [...column.tasks];
        let removedIndex = null;
        let addedIndex = null;

        if (column.id === columnFrom) {
          removedIndex = indexFrom;
        }

        if (column.id === columnTo) {
          const isMultiSelect = ['multiselect', 'array'].includes(this.groupingFieldInfo.type);
          const movedToAnotherColumn = columnFrom !== columnTo;
          const taskAlreadyInColumnTo = taskToOptions({ task: dragItem, groupingFieldInfo: this.groupingFieldInfo}).includes(columnTo);

          if (!(isMultiSelect && movedToAnotherColumn && taskAlreadyInColumnTo)) {
            addedIndex = indexTo;
          }
        }

        if (removedIndex === null && addedIndex === null) return { ...column };

        const updatedTasksData = applyDrag(updatedTasks, {
          columnId: column.id,
          removedIndex,
          addedIndex,
          payload: dragItem,
        });

        updatedTasks = updatedTasksData.updated;

        const sortFields = { ...this.sortingFieldInfo };

        // если есть кастомая сортировка, то обновляем данные в vuex и отправляет xhr
        if (sortFields.key === undefined) {
          // sortFields = { key: 'custom' };
          globalStore.dispatch('board/setCustomSort', {
            columnId: column.id,
            tasks: updatedTasks,
            addedIndex,
            fullIndexing: updatedTasksData.fullIndexing,
            dragItem,
          });
        }

        if (sortFields.key) {
          updatedTasks.sort(compareTasks({
            ...sortFields,
            resources: this.getResourcesByProjectId(this.ganttId),
          }));
        }

        let totalHeight = 0;

        updatedTasks.forEach((task, i) => {
          task.offsetTop = totalHeight + (i ? 8 : 0);
          totalHeight += task.taskHeight + (i ? 8 : 0);
        });

        return {
          ...column,
          totalHeight,
          tasks: updatedTasks,
        };
      });

      this.columns = updatedColumns;

      if (columnFrom !== columnTo) {
        const task = dragItem;
        const key = this.groupingFieldInfo.isCustom ? this.groupingFieldInfo.customFieldId : this.groupingFieldInfo.key;
        let value = '';

        if (key === 'resources') {
          let newResIds = [];

          if (columnTo === constants.COMMON_EMPTY) {
            newResIds.push(columnTo);
          } else {
            newResIds = taskToOptions({ task, groupingFieldInfo: this.groupingFieldInfo}).filter(optId => optId !== columnFrom);
            if (!newResIds.includes(columnTo)) {
              newResIds.push(columnTo);
            }
          }

          changeAssign({
            task: {
              ...task,
              columnId: columnFrom,
            },
            rawValue: newResIds,
            $store: globalStore,
          });

          return;
        }

        value = prepareTaskValue({
          task: { ...task, columnId: columnFrom },
          key,
          rawValue: columnTo,
          field: this.groupingFieldInfo,
        });

        this.taskChange({
          taskData: task,
          key,
          value,
        });
      }
    },
    getColumnPayload(index) {
      return this.columns[index];
    },
    onCollapse(colId) {
      const newCollapsedState = {
        ...this.collapsed,
        [colId]: !this.collapsed[colId],
      };

      this.collapsed = newCollapsedState;

      globalStore.dispatch('board/setBoardConfig', {
        config: {
          grouping: [{
            ...globalStore.getters['board/currentConfig'].grouping[0],
            order: this.columns.map(({ id }, i) => ({
              id,
              order: i + 1,
              isCollapsed: newCollapsedState[id] || false,
            })),
          }],
        },
      });

      if (this.readOnly) return;
    },
    setColWidth(container, columnCount) {
      let colWidth = Math.floor((container.offsetWidth - 32 - (columnCount - 1) * 12) / columnCount);

      if (colWidth < this.minColWidth) {
        colWidth = this.minColWidth;
      } else if (colWidth > this.maxColWidth) {
        colWidth = this.maxColWidth;
      }

      this.colWidth = colWidth;
    },
    moveColumn(column, removedIndex, addedIndex) {
      this.onColumnDrop({
        addedIndex,
        removedIndex,
        payload: column,
      });
    },
    editField() {
      if (!rights.account.hasRight('custom_fields_edit') || !rights.account.hasRight('custom_fields_delete')) return;

      app.trigger('columnsBar:open', {
        view: 'editing',
        activeTab: null,
        currencyEditing: false,
        isBoard: true,
        columnId: `${this.groupingFieldInfo.customFieldId}`,
      });
    },
    deleteField() {
      if (!rights.account.hasRight('custom_fields_edit') || !rights.account.hasRight('custom_fields_delete')) return;

      app.trigger('columnsBar:deleteColumn', {
        isBoard: true,
        columnId: `${this.groupingFieldInfo.customFieldId}`,
      });
    },
    taskChange({ taskData, key: property, value: payload }) {
      const storeTask = this.storeTasks.find(task => task.id === taskData.id);

      if (!storeTask) return;

      TaskSettingsController.setTaskData(storeTask);

      if (isNaN(property)) {
        TaskSettingsController.updateTaskDataPartially({
          property,
          payload,
        });
      } else {
        const column = this.getCustomColumnById(property);

        TaskSettingsController.updateCustomField(column, payload);
      }
    },
    toggleContextMenu(item, type, menu, target = null, side = '') {
      const targetData = target?.getBoundingClientRect(); // top level
      let position;

      // second level
      if (targetData && side) {
        position = {
          bottom: targetData.bottom,
          height: targetData.height,
          left: targetData.left,
          right: targetData.right,
          top: targetData.top - 7,
          width: targetData.width,
          x: targetData.x,
          y: targetData.y,

        };
        // can't use because it's not enumerable getBoundingClientRect()
        // Object.assign(targetData, { top: targetData.top });
      }
      globalStore.dispatch('board/setContextMenu', {
        item,
        type,
        menu,
        position: position || targetData || null,
        side,
      });
    },
    showTaskSettings(taskId, columnId, section) {
      if (section) {
        window.taskSettingsData = {
          initOptions: {
            selectComment: section === 'comments',
            selectAttachments: section === 'attachments',
          },
        };
      }

      setTimeout(() => {
        routerHelper.pushByNameAndParams('taskRoute', {
          mode: routerHelper.getCurrentRoute().params.mode,
          taskId,
        });
      });

      globalStore.dispatch('board/setTaskSettingsInfo', { taskId, columnId });

      userExtAnalytics.log('gantt_task_settings_open', {
        from: routerHelper.analyticsFromRoute(),
        route: `${routerHelper.analyticsRoute()}/ClickOnTasks`,
      });
    },
    async createTask(name, columnId, columnComponent) {
      name = name.trim();
      const parentId = +Object.keys(this.tasksMap).find((taskId) => this.tasksMap[taskId].type === 'project' && !this.tasksMap[taskId].parent);

      const newTask = {
        text: name.trim() || __('gantt_new_sibling_task'),
        gantt_id: this.ganttId,
        parent: parentId,
        type: constants.TASK_TYPES.task,
        start_date: new Date(),
        // color: '1',
      };

      if (this.groupingFieldInfo.key === 'status') {
        newTask.status = columnId;
      }

      if (this.groupingFieldInfo.key === 'priority') {
        newTask.priority = columnId;
      }

      if (this.groupingFieldInfo.key === 'resources' && !this.groupingFieldInfo.isCustom && columnId !== 'empty') {
        const value = this.projectResources[columnId].resourceProjects.find(p => p.projectId === this.ganttId)?.type === 1 ? 8 : 0;

        newTask.changedResources = [{
          action: 'insert',
          resource_id: columnId,
          value,
        }];
      }

      if (this.groupingFieldInfo.isCustom && columnId !== 'empty') {
        const fieldValue = [columnId];

        newTask.customFields = [{
          customColumnId: this.groupingFieldInfo.customFieldId,
          value: fieldValue,
          isOptions: Array.isArray(fieldValue),
        }];
      }

      this.draftTasks.splice(this.draftTasks.length, 0, {
        ...newTask,
        id: 'draft',
        type: 'task',
        color: '1',
        status: newTask.status || 1,
        priority: 7,
        resources: newTask.changedResources ? [{ ...newTask.changedResources[0] }] : [],
        end_date: new Date(),
        hasAttachments: 0,
        hasComments: 0,
        newComments: null,
        columnId
      });

      columnComponent.emptyTask = false;

      const createdTaskIds = await globalStore.dispatch('tasksModel/createTasks', {
        tasks: [newTask],
        sourceEvent: {
          type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_CREATE,
          sourceId: null,
        },
        fromGantt: false,
      });

      createdTaskIds.forEach(taskId => {
        const draftIndex = this.draftTasks.findIndex(t => t.id === 'draft' && !t.taskId);

        if (draftIndex === -1) return;

        this.draftTasks[draftIndex] = {
          ...this.draftTasks[draftIndex],
          taskId,
        };

        const createdTask = this.storeTasks.find(task => task.id === taskId);
        createdTask && this.cleanupDraftTask(createdTask);
      });
    },
    showDeleteConfirmationPopup(task) {
      const isJira = !!task.jira_key;
      const localeKey = `settings_msg_delete_task${isJira ? '_jira' : ''}`;

      const taskName = __(localeKey, { taskName: `<b>${customHelper.formatTaskName(task.text)}</b>` });

      this.confirmDeletionData = {
        id: task.id,
        gantt_id: this.ganttId,
        text: `<div>${taskName}</div>`,
        showPopup: true,
      };
    },
    async handleConfirmDeleteTask() {
      userExtAnalytics.log('task_settings_action', { action: 'delete' });

      const tasksToDelete = [{ id: this.confirmDeletionData.id, gantt_id: this.confirmDeletionData.gantt_id }];

      await globalStore.dispatch('tasksModel/deleteTasks', {
        tasks: tasksToDelete,
        sourceEvent: {
          type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_DELETE,
          sourceId: this.confirmDeletionData.id,
        },
        fromGantt: false,
      });

      userExtAnalytics.log('gantt_task_delete_confirmed');

      globalStore.commit('comments/deleteDraft', {
        projectId: +this.confirmDeletionData.gantt_id,
        taskId: +this.confirmDeletionData.id,
      });

      this.confirmDeletionData = {};
    },
    handleCancelDeleteTask() {
      userExtAnalytics.log('gantt_task_delete_cancelled');
      this.confirmDeletionData = {};
    },
    isFieldDisabled(key, taskData) {
      return key in this.disabledFieldsWithReasons(taskData);
    },
    disabledFieldsWithReasons(taskData) {
      const disabledFields = [];
      const disabledFieldsReasons = {};

      if (TaskSettingsController.checkByLinkDependency(taskData, 'start_date')) {
        disabledFields.push('start_date');
        if (this.rightsAccessCache.static_fields) {
          disabledFieldsReasons.start_date = 'why_can_not_set_it_case4';
        }
      }

      if (TaskSettingsController.checkByLinkDependency(taskData.id, 'end_date')) {
        disabledFields.push('end_date');
        if (this.rightsAccessCache.static_fields) {
          disabledFieldsReasons.end_date = 'why_can_not_set_it_case5';
        }
      }

      if (!this.rightsAccessCache.static_fields) {
        disabledFields.push(
          ...[
            'priority', 'estimation',
            'duration', 'start_date', 'end_date',
            'deadline', 'resources', 'text', 'note',
            'color', 'dependencies',
          ],
        );
      }

      if (!this.rightsAccessCache.static_fields_4_only && !this.rightsAccessCache.static_fields) {
        disabledFields.push(
          ...['progress', 'time_log', 'status', 'attachments'],
        );
      }

      if (!this.rightsAccessCache.delete_task) {
        disabledFields.push('delete_task');
      }

      if (!this.rightsAccessCache.comments) {
        disabledFields.push('comments');
      }

      if (!this.rightsAccessCache.create_task) {
        disabledFields.push('type');
      }

      const uniqueDisabledFields = [...new Set(disabledFields)];
      const disabledFieldsWithReasons = uniqueDisabledFields.reduce((result, fieldName) => {
        if (disabledFieldsReasons[fieldName]) {
          result[fieldName] = disabledFieldsReasons[fieldName];
        } else {
          result[fieldName] = null;
        }

        return result;
      }, {});

      return disabledFieldsWithReasons;
    },
    taskDragStart({ task, columnFrom }) {
      this.taskDragging = true;
      this.dragPutRestricted = taskToOptions({ task, groupingFieldInfo: this.groupingFieldInfo }).filter(opt => opt !== columnFrom);
    },
    onColumnDragStart() {
      this.hideFilterPopup();
      this.hideProjectSettingsPopup();
      this.columnDragging = true;
    },
    getProjectName() {
      return this.projectName;
    },
    getFormattedDate(value) {
      if (!value) return '';
      const date = new Date(value);
      let format = dateHelper.getDateFormat();

      return webix.Date.dateToStr(format)(date);
    },
    calculateDatesWidth() {
      const datesDivStyle = `
        position: absolute;
        visibility: hidden;
        padding: 5px 12px;
        font-family: "Lato-Regular", sans-serif;
        font-size: 12px;
        line-height: 18px;
      `;
      const datesDiv = document.createElement('div');
      datesDiv.setAttribute('style', datesDivStyle);
      datesDiv.textContent = this.getFormattedDate(new Date).trim() + '-' + this.getFormattedDate(new Date).trim();

      this.$refs.boardContainer.appendChild(datesDiv);

      return datesDiv.offsetWidth + 1;
    },
    calculateTaskHeight(taskId) {
        let taskHeight = 146; // минимальная высота карточки

        if (!['status', 'priority'].includes(this.groupingFieldInfo.key)) {
          taskHeight += 32;
        }

        if (this.tasksMap[this.tasksMap[taskId]?.parent]?.parent) {
          taskHeight += 18;
        }

        return taskHeight;
    },
    hideFilterPopup() {
      app.trigger('hideFilterPopup');
    },
    hideProjectSettingsPopup() {
      app.trigger('closeWindowWithAnimation');
    },
    clearNewTasksPool() {
      this.boardTasks = [...this.boardTasks, ...this.newTasksPool];
      globalStore.dispatch('board/clearNewTasksPool');
    },
    getCalendarConfig() {
      return this.calendarConfig;
    },
    async getClosestDate(params) {
      if (!calendarModule.isWorkDay(params.date, this.calendarConfig)) {
        return CalendarWorker.calculate(params, 'getClosestWorkTime');
      }

      return params.date;
    },
    isWorkDay(date) {
      return calendarModule.isWorkDay(date, this.calendarConfig);
    },
    checkPricingAccessOverdue() {
      return checkPricingAccess('overdue_tasks');
    },
    cleanupDraftTask(payload) {
      let draftIndex = this.draftTasks.findIndex(t => t.taskId === payload.id);

      if (draftIndex === -1) return;

      const newData = {
        ...this.draftTasks[draftIndex],
        ...payload
      };

      this.draftTasks[draftIndex] = newData;

      this.draftCleanupTimeout[newData.id] && clearTimeout(this.draftCleanupTimeout[newData.id]);
      this.draftCleanupTimeout[newData.id] = setTimeout(() => {
        globalStore.dispatch('board/addNewTaskToPool', newData.id);
        this.updateTaskInColumns(newData, 'add');
        let draftIndex = this.draftTasks.findIndex(t => t.taskId === newData.id);
        this.draftTasks.splice(draftIndex, 1);
        delete this.draftCleanupTimeout[newData.id];
      }, 500);
    },
    closeContextMenus() {
      globalStore.dispatch('board/clearContextMenus');
    },
    hideResourcePopup() {
      app.trigger('hideResourcesListForTask');
    },
  },
};
</script>

<style module src="./less/board.less"></style>
